/** forge.motion.v1 — structured playable motion. Never play raw AI JSON. */

import { parseMotionMedia } from "./media-mockup";

export const MOTION_SCHEMA = "forge.motion.v1" as const;

export const BUILTIN_FX_IDS = [
  "orbs",
  "mesh",
  "aurora",
  "stars",
  "sparkles",
  "grid",
  "ribbons",
  "hex",
  "rain",
  "flame",
  "scan",
  "none",
] as const;

export type BuiltinFxId = (typeof BUILTIN_FX_IDS)[number];

export type ColorFrom = "accent" | "secondary" | "magenta" | "text" | "glow";

export type ParticleShape = "dot" | "streak" | "ember" | "hex" | "star" | "wave" | "flake" | "slash";
export type ParticleDir = "up" | "down" | "left" | "right" | "drift";
export type WashKind = "mesh" | "aurora" | "ribbons" | "fog" | "pulse";
export type DriftSpeed = "slow" | "med" | "fast";
export type ScanAxis = "x" | "y";

export type MotionLayer =
  | {
      type: "orbs";
      count: number;
      size: number;
      blur: number;
      color: ColorFrom;
      opacity: number;
      drift: DriftSpeed;
    }
  | {
      type: "particles";
      count: number;
      shape: ParticleShape;
      direction: ParticleDir;
      speed: number;
      color: ColorFrom;
      length?: number;
    }
  | {
      type: "wash";
      kind: WashKind;
      speed: number;
      colors: ColorFrom[];
    }
  | {
      type: "grid";
      perspective: number;
      scanline: boolean;
      color: ColorFrom;
    }
  | {
      type: "scan";
      axis: ScanAxis;
      period: number;
      color: ColorFrom;
    }
  | {
      type: "wallpaper";
      fit: "cover" | "contain";
      drift: boolean;
      opacity: number;
    }
  | {
      type: "rays";
      count: number;
      speed: number;
      color: ColorFrom;
    }
  | {
      type: "rings";
      count: number;
      speed: number;
      color: ColorFrom;
    }
  | { type: "none" };

export interface MotionGlyphOverlay {
  chars: string;
  count: number;
  direction: ParticleDir;
}

export interface MotionSpec {
  schema: typeof MOTION_SCHEMA;
  id: string;
  name: string;
  blurb: string;
  prompt: string;
  layers: MotionLayer[];
  nearestBuiltin: BuiltinFxId;
  reducedMotion: "none" | "soften";
  /** Compiled playable CSS (token-colored). Same sheet in preview and suite. */
  css?: string;
  glyphs?: MotionGlyphOverlay;
  /** CSS graphics graph — the real authored artifact. */
  graph?: import("./css-graph").CssGraph;
  /** Generate lane: still + motion clip hosted by a CSS mockup. */
  media?: import("./media-mockup").MotionMedia;
}

const COLOR_FROM = new Set<ColorFrom>([
  "accent",
  "secondary",
  "magenta",
  "text",
  "glow",
]);
const SHAPES = new Set<ParticleShape>(["dot", "streak", "ember", "hex", "star", "wave", "flake", "slash"]);
const DIRS = new Set<ParticleDir>(["up", "down", "left", "right", "drift"]);
const WASH = new Set<WashKind>(["mesh", "aurora", "ribbons", "fog", "pulse"]);
const DRIFT = new Set<DriftSpeed>(["slow", "med", "fast"]);
const AXES = new Set<ScanAxis>(["x", "y"]);
const BUILTIN = new Set<string>(BUILTIN_FX_IDS);

export function isBuiltinFx(id: string): id is BuiltinFxId {
  return BUILTIN.has(id);
}

export function slugifyId(raw: string, reservedPrefix = "fx-"): string {
  const s = String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
  if (!s) return `${reservedPrefix}motion`;
  if (BUILTIN.has(s) || s === "new" || s === "default") {
    return `${reservedPrefix}${s}`;
  }
  return s;
}

function clamp01(n: unknown, fallback = 0.5): number {
  const x = typeof n === "number" ? n : Number(n);
  if (Number.isNaN(x)) return fallback;
  return Math.max(0, Math.min(1, x));
}

function clampInt(n: unknown, min: number, max: number, fallback: number): number {
  const x = typeof n === "number" ? n : Number(n);
  if (Number.isNaN(x)) return fallback;
  return Math.max(min, Math.min(max, Math.floor(x)));
}

function asColor(v: unknown): ColorFrom {
  if (typeof v === "string" && COLOR_FROM.has(v as ColorFrom)) return v as ColorFrom;
  return "accent";
}

function clipStr(v: unknown, max: number): string {
  return String(v ?? "")
    .replace(/<[^>]*>/g, "")
    .replace(/url\s*\(|expression\s*\(|javascript:/gi, "")
    .slice(0, max);
}

/** AI often emits wave/stream/water — those used to collapse to tiny invisible dots. */
export function coerceParticleShape(raw: unknown, direction: ParticleDir): ParticleShape {
  const s = String(raw || "").toLowerCase().trim();
  if (SHAPES.has(s as ParticleShape)) return s as ParticleShape;
  if (/wave|stream|water|flow|current|river|tide|aqua/.test(s)) return "wave";
  if (/ember|fire|flame/.test(s)) return "ember";
  if (/hex/.test(s)) return "hex";
  if (/star/.test(s)) return "star";
  if (/flake|snow/.test(s)) return "flake";
  if (/slash/.test(s)) return "slash";
  if (/streak|drop|line|rain/.test(s)) return "streak";
  if (direction === "left" || direction === "right") return "wave";
  if (direction === "down") return "streak";
  return "dot";
}

export function hasDiscreteGraphic(layers: MotionLayer[]): boolean {
  return layers.some(
    (l) =>
      l.type === "particles" ||
      l.type === "orbs" ||
      l.type === "grid" ||
      l.type === "scan" ||
      l.type === "wallpaper" ||
      l.type === "rays" ||
      l.type === "rings",
  );
}

function parseGraph(raw: unknown): MotionSpec["graph"] {
  if (!raw || typeof raw !== "object") return undefined;
  const o = raw as Record<string, unknown>;
  if (!Array.isArray(o.ops) || o.ops.length > 8) return undefined;
  return raw as MotionSpec["graph"];
}

function parseMotionMediaField(raw: unknown): MotionSpec["media"] {
  return parseMotionMedia(raw);
}

function parseGlyphs(raw: unknown): MotionGlyphOverlay | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const o = raw as Record<string, unknown>;
  const chars = String(o.chars || "").replace(/\s+/g, "").slice(0, 12);
  if (!chars) return undefined;
  const direction = DIRS.has(o.direction as ParticleDir) ? (o.direction as ParticleDir) : "down";
  return { chars, count: clampInt(o.count, 6, 48, 24), direction };
}

export function motifParticleLayer(text: string): MotionLayer {
  const blob = (text || "").toLowerCase().replace(/bianary|bianry/g, "binary");
  if (/binary|matrix|code rain|falling code|digits?/.test(blob)) {
    return {
      type: "particles",
      count: 32,
      shape: "streak",
      direction: "down",
      speed: 0.7,
      color: "accent",
      length: 0.55,
    };
  }
  if (/waterfall/.test(blob)) {
    return {
      type: "particles",
      count: 28,
      shape: "streak",
      direction: "down",
      speed: 0.68,
      color: "secondary",
      length: 0.6,
    };
  }
  if (/water|ocean|sea|river|lake|tide|aqua|liquid|wave|current|\bstreams?\b/.test(blob)) {
    return {
      type: "particles",
      count: 28,
      shape: "wave",
      direction: "right",
      speed: 0.55,
      color: "secondary",
      length: 0.7,
    };
  }
  if (/ember|flame|fire|magma|lava/.test(blob)) {
    return {
      type: "particles",
      count: 18,
      shape: "ember",
      direction: "up",
      speed: 0.62,
      color: "accent",
      length: 0.3,
    };
  }
  return {
    type: "particles",
    count: 26,
    shape: "dot",
    direction: "drift",
    speed: 0.45,
    color: "secondary",
    length: 0.35,
  };
}

function parseLayer(raw: unknown): MotionLayer | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const type = String(o.type || "");
  if (type === "none") return { type: "none" };
  if (type === "orbs") {
    return {
      type: "orbs",
      count: clampInt(o.count, 1, 6, 3),
      size: clamp01(o.size, 0.55),
      blur: clamp01(o.blur, 0.5),
      color: asColor(o.color),
      opacity: clamp01(o.opacity, 0.7),
      drift: DRIFT.has(o.drift as DriftSpeed) ? (o.drift as DriftSpeed) : "med",
    };
  }
  if (type === "particles") {
    const direction = DIRS.has(o.direction as ParticleDir)
      ? (o.direction as ParticleDir)
      : "drift";
    const shape = coerceParticleShape(o.shape, direction);
    return {
      type: "particles",
      count: clampInt(o.count, 4, 80, 24),
      shape,
      direction,
      speed: clamp01(o.speed, 0.55),
      color: asColor(o.color),
      length: clamp01(o.length, 0.45),
    };
  }
  if (type === "wash") {
    const kind = WASH.has(o.kind as WashKind) ? (o.kind as WashKind) : "mesh";
    const colors = Array.isArray(o.colors)
      ? (o.colors as unknown[]).map(asColor).slice(0, 3)
      : [];
    return {
      type: "wash",
      kind,
      speed: clamp01(o.speed, 0.45),
      colors: colors.length ? colors : ["accent", "secondary"],
    };
  }
  if (type === "grid") {
    return {
      type: "grid",
      perspective: clamp01(o.perspective, 0.65),
      scanline: o.scanline !== false,
      color: asColor(o.color),
    };
  }
  if (type === "scan") {
    return {
      type: "scan",
      axis: AXES.has(o.axis as ScanAxis) ? (o.axis as ScanAxis) : "x",
      period: clamp01(o.period, 0.5),
      color: asColor(o.color),
    };
  }
  if (type === "wallpaper") {
    return {
      type: "wallpaper",
      fit: o.fit === "contain" ? "contain" : "cover",
      drift: o.drift === true,
      opacity: clamp01(o.opacity, 0.45),
    };
  }
  if (type === "rays") {
    return {
      type: "rays",
      count: clampInt(o.count, 4, 16, 8),
      speed: clamp01(o.speed, 0.45),
      color: asColor(o.color),
    };
  }
  if (type === "rings") {
    return {
      type: "rings",
      count: clampInt(o.count, 2, 10, 4),
      speed: clamp01(o.speed, 0.4),
      color: asColor(o.color),
    };
  }
  return null;
}

export function nearestFromLayers(layers: MotionLayer[]): BuiltinFxId {
  const first = layers[0];
  if (!first || first.type === "none") return "none";
  if (first.type === "orbs") return "orbs";
  if (first.type === "grid") return "grid";
  if (first.type === "scan") return "scan";
  if (first.type === "wallpaper") return "mesh";
  if (first.type === "rays") return "scan";
  if (first.type === "rings") return "orbs";
  if (first.type === "wash") {
    if (first.kind === "aurora") return "aurora";
    if (first.kind === "ribbons") return "ribbons";
    return "mesh";
  }
  if (first.type === "particles") {
    if (first.shape === "ember") return "flame";
    if (first.shape === "streak" || first.shape === "wave" || first.shape === "slash") return "rain";
    if (first.shape === "flake") return "sparkles";
    if (first.shape === "hex") return "hex";
    if (first.shape === "star") return "stars";
    if (first.direction === "up") return "sparkles";
    if (first.direction === "down" || first.direction === "left" || first.direction === "right") {
      return "rain";
    }
    return "sparkles";
  }
  return "orbs";
}

export function defaultOrbsSpec(prompt = ""): MotionSpec {
  return {
    schema: MOTION_SCHEMA,
    id: "orbs",
    name: "Orbs",
    blurb: "Soft glowing orbs drifting.",
    prompt,
    layers: [
      {
        type: "orbs",
        count: 3,
        size: 0.55,
        blur: 0.55,
        color: "accent",
        opacity: 0.75,
        drift: "med",
      },
    ],
    nearestBuiltin: "orbs",
    reducedMotion: "soften",
  };
}

export function builtinToSpec(id: string, extra?: Partial<MotionSpec>): MotionSpec {
  const bid: BuiltinFxId = isBuiltinFx(id) ? id : "orbs";
  const nowName: Record<BuiltinFxId, string> = {
    orbs: "Orbs",
    mesh: "Mesh",
    aurora: "Aurora",
    stars: "Stars",
    sparkles: "Sparkles",
    grid: "Grid",
    ribbons: "Ribbons",
    hex: "Hex",
    rain: "Rain",
    flame: "Flame",
    scan: "Scan",
    none: "Still",
  };
  const layers: MotionLayer[] =
    bid === "none"
      ? [{ type: "none" }]
      : bid === "orbs"
        ? [defaultOrbsSpec().layers[0]!]
        : bid === "mesh"
          ? [{ type: "wash", kind: "mesh", speed: 0.45, colors: ["accent", "secondary", "magenta"] }]
          : bid === "aurora"
            ? [{ type: "wash", kind: "aurora", speed: 0.4, colors: ["accent", "secondary"] }]
            : bid === "ribbons"
              ? [{ type: "wash", kind: "ribbons", speed: 0.5, colors: ["accent", "secondary"] }]
              : bid === "grid"
                ? [{ type: "grid", perspective: 0.7, scanline: true, color: "accent" }]
                : bid === "scan"
                  ? [{ type: "scan", axis: "x", period: 0.5, color: "accent" }]
                  : bid === "flame"
                    ? [
                        {
                          type: "particles",
                          count: 18,
                          shape: "ember",
                          direction: "up",
                          speed: 0.6,
                          color: "accent",
                          length: 0.3,
                        },
                      ]
                    : bid === "rain"
                      ? [
                          {
                            type: "particles",
                            count: 24,
                            shape: "streak",
                            direction: "down",
                            speed: 0.7,
                            color: "accent",
                            length: 0.55,
                          },
                        ]
                      : bid === "hex"
                        ? [
                            {
                              type: "particles",
                              count: 12,
                              shape: "hex",
                              direction: "drift",
                              speed: 0.3,
                              color: "accent",
                            },
                          ]
                        : bid === "stars"
                          ? [
                              {
                                type: "particles",
                                count: 40,
                                shape: "star",
                                direction: "drift",
                                speed: 0.2,
                                color: "text",
                              },
                            ]
                          : [
                              {
                                type: "particles",
                                count: 30,
                                shape: "dot",
                                direction: "up",
                                speed: 0.55,
                                color: "secondary",
                              },
                            ];
  return {
    schema: MOTION_SCHEMA,
    id: bid,
    name: extra?.name || nowName[bid],
    blurb: extra?.blurb || nowName[bid],
    prompt: extra?.prompt || bid,
    layers,
    nearestBuiltin: bid,
    reducedMotion: extra?.reducedMotion || "soften",
  };
}

export function validateMotionSpec(raw: unknown): {
  ok: boolean;
  spec: MotionSpec;
  errors: string[];
} {
  const errors: string[] = [];
  const fallback = defaultOrbsSpec();

  if (raw == null) {
    return { ok: false, spec: fallback, errors: ["not an object"] };
  }

  // Old AI shape: { id: "rain" }
  if (typeof raw === "object" && raw && !("layers" in (raw as object))) {
    const o = raw as Record<string, unknown>;
    const id = String(o.id || o.defaultFx || "").toLowerCase().trim();
    if (isBuiltinFx(id)) {
      const spec = builtinToSpec(id, {
        name: o.name ? String(o.name) : undefined,
        blurb: o.blurb ? String(o.blurb) : undefined,
        prompt: o.prompt ? String(o.prompt) : id,
      });
      return { ok: true, spec, errors: [] };
    }
    if (!("schema" in o)) {
      return { ok: false, spec: fallback, errors: ["missing layers"] };
    }
  }

  if (!raw || typeof raw !== "object") {
    return { ok: false, spec: fallback, errors: ["not an object"] };
  }
  const o = raw as Record<string, unknown>;
  const parsed: MotionLayer[] = [];
  const list = Array.isArray(o.layers) ? o.layers : [];
  if (!list.length) errors.push("layers empty");
  for (const item of list) {
    const layer = parseLayer(item);
    if (layer) parsed.push(layer);
  }

  // none + others → still only
  if (parsed.some((l) => l.type === "none") && parsed.length > 1) {
    parsed.splice(0, parsed.length, { type: "none" });
  }

  let layers = parsed.slice(0, 3);
  const inboundMediaEarly = parseMotionMediaField(o.media);
  if (!layers.length && inboundMediaEarly?.mockup) {
    layers = [{ type: "wallpaper", fit: "cover", drift: true, opacity: 0.92 }];
    errors.length = 0;
  }
  if (!layers.length) {
    return { ok: false, spec: fallback, errors: errors.length ? errors : ["no playable layers"] };
  }

  const blob = `${o.prompt || ""} ${o.name || ""} ${o.blurb || ""} ${o.id || ""}`;
  const customId = !isBuiltinFx(String(o.id || ""));
  if (
    !inboundMediaEarly?.mockup &&
    !hasDiscreteGraphic(layers) &&
    !layers.every((l) => l.type === "none") &&
    (customId || /water|stream|binary|wave|ocean|river/.test(blob.toLowerCase()))
  ) {
    layers = [...layers, motifParticleLayer(blob)].slice(0, 3);
  }

  const nearestRaw = String(o.nearestBuiltin || "");
  const nearest: BuiltinFxId = isBuiltinFx(nearestRaw)
    ? nearestRaw
    : nearestFromLayers(layers);

  let id = String(o.id || nearest || "fx-motion");
  if (layers.length === 1 && layers[0]!.type !== "none" && isBuiltinFx(id) === false) {
    id = slugifyId(id);
  } else if (layers.length > 1) {
    id = slugifyId(id);
  } else if (!isBuiltinFx(id) && layers[0]?.type !== "none") {
    id = slugifyId(id);
  } else if (layers[0]?.type === "none") {
    id = "none";
  }

  const media = inboundMediaEarly;

  const spec: MotionSpec = {
    schema: MOTION_SCHEMA,
    id,
    name: clipStr(o.name || id, 80) || id,
    blurb: clipStr(o.blurb || "", 160),
    prompt: clipStr(o.prompt || "", 2000),
    layers,
    nearestBuiltin: nearest,
    reducedMotion: o.reducedMotion === "none" ? "none" : "soften",
    css: undefined,
    glyphs: parseGlyphs(o.glyphs),
    graph: parseGraph(o.graph),
    media,
  };

  return { ok: errors.length === 0, spec, errors };
}

export function scaleSpec(
  spec: MotionSpec,
  opts: { particleScale?: number; compact?: boolean; reduced?: boolean },
): MotionSpec {
  if (opts.reduced) {
    return { ...spec, layers: [{ type: "none" }] };
  }
  const p = opts.particleScale ?? (opts.compact ? 12 / 80 : 1);
  const layers = spec.layers.map((l) => {
    if (l.type === "particles") {
      const cap = opts.compact ? 12 : 80;
      return { ...l, count: Math.max(4, Math.min(cap, Math.round(l.count * p))) };
    }
    if (l.type === "orbs" && opts.compact) {
      return { ...l, count: Math.min(2, l.count) };
    }
    if (l.type === "grid" && opts.compact) {
      return { ...l, scanline: false };
    }
    return l;
  });
  return { ...spec, layers };
}
